import * as pdfMake from "pdfmake/build/pdfmake.js";

import { ResidentInfo, SendDocumentReplyGetModel } from "../services/SendDocuments";
import DateUtil from "@/plugins/date-util/DateUtil";

/**
 * ### 文書送付履歴のPDF操作
 */
export class SendDocumentsReportPdf {
  dateUtil: DateUtil;
  normalFont: string;

  /**
   * ### コンストラクター
   *
   * - 渡されたフォントをPdfMakeに設定する
   */
  constructor(normalFont: string) {
    this.dateUtil = new DateUtil();
    this.normalFont = normalFont;

    pdfMake.vfs = { "SendDocumentPdf.ttf": this.normalFont };
    pdfMake.fonts = {
      SendDocumentPdf: {
        normal: "SendDocumentPdf.ttf",
        bold: "SendDocumentPdf.ttf"
      }
    };

    return;
  }

  /**
   * ### 文書送付PDFを作成してダウンロードさせる
   *
   * @param items 印刷対象の文書送付
   * @param facilityName 施設名
   * @param residentName 利用者名
   * @param fileName ファイル名
   */
  public createAndDownloadPdf(items: Array<SendDocumentsReportPreviewTableItem>, facilityName: string, fileName: string): void {
    const docDefinition: any = {
      styles: this.createStyles(),
      defaultStyle: this.createDefaultStyle(),
      pageSize: "A4",
      pageOrientation: "portrait",
      pageMargins: [27, 14, 27, 27], // [left, top, right, bottom]
      info: this.createInfo(),
      content: this.createContent(items, facilityName),
      footer: this.createFooter()
    };

    pdfMake.createPdf(docDefinition).download(fileName);

    return;
  }

  /**
   * ### 文書送付PDF（詳細形式：1件ごと・添付書類画像付き）を作成してダウンロードさせる
   */
  public createAndDownloadDetailPdf(items: Array<SendDocumentsReportPreviewTableItem>, facilityName: string, fileName: string, imagesMap: Map<string, Array<string>>): void {
    const docDefinition: any = {
      styles: this.createStyles(),
      defaultStyle: this.createDefaultStyle(),
      pageSize: "A4",
      pageOrientation: "portrait",
      pageMargins: [27, 14, 27, 27], // [left, top, right, bottom]
      info: this.createInfo(),
      content: this.createDetailContent(items, facilityName, imagesMap),
      footer: this.createFooter()
    };

    pdfMake.createPdf(docDefinition).download(fileName);

    return;
  }

  /**
   * ### 詳細形式の印刷情報を作成する
   */
  private createDetailContent(items: Array<SendDocumentsReportPreviewTableItem>, facilityName: string, imagesMap: Map<string, Array<string>>): Array<any> {
    const contents: Array<any> = [];

    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      const isFirstItem = i === 0;

      // 1件ごとに新しいページから開始（1件目以外）
      const headerContents = this.createHeader(facilityName);
      if (!isFirstItem && headerContents.length > 0) {
        headerContents[0].pageBreak = "before";
      }
      contents.push(...headerContents);

      // 1件分の1行テーブルを作成
      contents.push(this.createSingleRowTable(item));

      // アイテムに関連する添付ファイル画像のリストを取得
      const itemKey = `${item.parentSendDocumentsMainId}_${item.sortKeyPartOne}_${item.sortKeyPartTwo}`;
      const itemImages = imagesMap.get(itemKey) || imagesMap.get(String(i)) || [];

      if (itemImages.length > 0) {
        const gridElements = this.createImagesGrid(itemImages);
        contents.push(...gridElements);
      }
    }

    return contents;
  }

  /**
   * ### 添付画像群（1〜9件）を1ページ内に集約配置するオブジェクトを作成する
   */
  private createImagesGrid(itemImages: Array<string>): Array<any> {
    if (!itemImages || itemImages.length === 0) {
      return [];
    }

    const count = Math.min(itemImages.length, 9);
    const validImages = itemImages.slice(0, count);

    // 行構成の定義 (1〜9件)
    const rowConfigurations: { [key: number]: number[] } = {
      1: [1],
      2: [2],
      3: [2, 1],
      4: [2, 2],
      5: [3, 2],
      6: [3, 3],
      7: [3, 3, 1],
      8: [3, 3, 2],
      9: [3, 3, 3]
    };

    const rowCounts = rowConfigurations[count] || [1];
    const numRows = rowCounts.length;

    // 1ページ内で画像を配置可能な最大領域
    const totalWidth = 538;
    const totalHeight = 540;

    // 行ごとの最大高さ
    const rowGap = 10;
    const availableHeightForRow = (totalHeight - (numRows - 1) * rowGap) / numRows;

    const resultGrid: Array<any> = [];
    let imageIndex = 0;

    for (let r = 0; r < numRows; r++) {
      const itemsInRow = rowCounts[r];
      const maxColsInLayout = Math.max(...rowCounts);
      const colGap = 10;

      // 3列または2列レイアウト時の基準幅
      const colWidth = (totalWidth - (maxColsInLayout - 1) * colGap) / maxColsInLayout;
      const fitWidth = Math.floor(colWidth);
      const fitHeight = Math.floor(availableHeightForRow);

      const columns: Array<any> = [];

      // 中央揃え用のマージン（この行の要素数が最大列数より少ない場合）
      if (itemsInRow < maxColsInLayout) {
        const rowWidth = itemsInRow * fitWidth + (itemsInRow - 1) * colGap;
        const leftSpacer = (totalWidth - rowWidth) / 2;
        columns.push({ text: "", width: leftSpacer });
      }

      for (let c = 0; c < itemsInRow; c++) {
        if (imageIndex >= validImages.length) break;
        const imgDataUrl = validImages[imageIndex];
        imageIndex++;

        columns.push({
          width: fitWidth,
          table: {
            widths: [fitWidth],
            body: [
              [
                {
                  image: imgDataUrl,
                  fit: [fitWidth, fitHeight],
                  alignment: "center"
                }
              ]
            ]
          },
          layout: {
            hLineWidth: () => 1,
            vLineWidth: () => 1,
            hLineColor: () => "#000000",
            vLineColor: () => "#000000",
            paddingLeft: () => 0,
            paddingRight: () => 0,
            paddingTop: () => 0,
            paddingBottom: () => 0
          },
          alignment: "center"
        });

        if (c < itemsInRow - 1) {
          columns.push({ text: "", width: colGap });
        }
      }

      resultGrid.push({
        columns: columns,
        margin: [0, r === 0 ? 10 : rowGap, 0, 0]
      });
    }

    return resultGrid;
  }

  /**
   * ### 1件分のテーブル情報を作成する
   */
  private createSingleRowTable(item: SendDocumentsReportPreviewTableItem): any {
    // 同意or確認済みの場合、連絡内容には固定文言を追加する
    let message = this.getMessagePrefix(item);
    if (item.message) {
      message += item.message;
    }

    const residentInfo: ResidentInfo = item.residentInfo as ResidentInfo;
    const tableContent: any = {
      style: "table",
      table: {
        headerRows: 1,
        widths: [78, 134, 90, 20, 78, 90]
      }
    };
    const body: Array<any> = [
      [
        { text: "送付日時", style: "tableHeader" },
        { text: "送付文書", style: "tableHeader" },
        { text: "利用者\n家族", style: "tableHeader" },
        { text: "返信状態", style: "tableHeader" },
        { text: "返信日時", style: "tableHeader" },
        { text: "返信時の連絡事項", style: "tableHeader" }
      ],
      [
        { text: this.convertDateText(item.sendDatetime), style: { alignment: "center" } },
        item.title,
        `${residentInfo.name}（${residentInfo.room}）\n${residentInfo.familyName}（${residentInfo.familyRelation}）`,
        { text: this.convertStatusText(item), style: { alignment: "center" } },
        { text: this.convertDateText(item.datetime), style: { alignment: "center" } },
        message
      ]
    ];
    tableContent.table.body = body;

    return tableContent;
  }

  /**
   * ### PDFのスタイルを作成する
   *
   * - 項目毎、または共通で使用できるスタイルを指定する
   */
  private createStyles(): any {
    return {
      datetime: {
        fontSize: 12,
        alignment: "right",
        margin: [0, 0, 0, 5]
      },
      title: {
        fontSize: 18,
        bold: true,
        alignment: "center",
        margin: [0, 0, 0, 18]
      },
      names: {
        fontSize: 12
      },
      table: {
        fontSize: 7,
        margin: [0, 10, 0, 0]
      },
      tableHeader: {
        bold: true,
        fillColor: "#EEEEEE",
        alignment: "center",
        margin: [2, 2, 2, 2]
      }
    };
  }

  /**
   * ### PDFの共通スタイルを作成する
   *
   * - 全体で共通で使用できるスタイルを指定する
   */
  private createDefaultStyle(): any {
    return {
      font: "SendDocumentPdf"
    };
  }

  /**
   * ### PDF文書情報を作成する
   */
  private createInfo(): any {
    return {
      title: "HitomeQコネクト 文書送付一覧",
      author: "KONICAMINOLA"
    };
  }

  /**
   * ### フッター情報を作成する
   *
   * - ページ番号を印字する
   */
  private createFooter(): Function {
    return (currentPage: number, pageCount: number) => {
      return { text: currentPage.toString() + " / " + pageCount + "ページ", style: { fontSize: 12, alignment: "center", margin: [0, 12, 0, 12] } };
    };
  }

  /**
   * ### 印刷情報を作成する
   *
   * - ヘッダー部
   *   - 作成日
   *   - タイトル
   *   - 施設名
   *   - 利用者名
   * - コンテンツ部(文書送付情報)
   */
  private createContent(items: Array<SendDocumentsReportPreviewTableItem>, facilityName: string): any {
    let contents: Array<any> = [];

    contents = this.createHeader(facilityName);
    contents.push(this.createTable(items));

    return contents;
  }

  /**
   * ### コンテンツのタイトル及び固定部分を作成する
   *
   * @param facilityName 施設名
   * @param residentName 利用者名
   */
  private createHeader(facilityName: string): Array<any> {
    const contents: Array<any> = [];

    // 日付
    contents.push({
      text: this.pdfCreateDateTime(),
      style: "datetime"
    });

    // タイトル
    contents.push({
      text: "HitomeQコネクト 文書送付一覧",
      style: "title"
    });

    // 施設名、利用者名
    contents.push({
      text: ["施設名：", { text: `${facilityName}`, bold: true }],
      style: "names"
    });

    return contents;
  }

  /**
   * ### 作成日時を「yyyy年 mm月 dd日」の形式の文字列にする
   */
  private pdfCreateDateTime(): string {
    const datetime: string = this.dateUtil.convertDateToString(new Date(), "yyyy年 mm月 dd日", false);
    return datetime;
  }

  /**
   * ### テーブル情報を作成する
   *
   * @param items 文書送付情報
   */
  private createTable(items: Array<SendDocumentsReportPreviewTableItem>): any {
    // テーブル
    const tableContent: any = {
      style: "table",
      table: {
        headerRows: 1,
        widths: [78, 134, 90, 20, 78, 90]
      }
    };
    const body: Array<any> = [
      [
        { text: "送付日時", style: "tableHeader" },
        { text: "送付文書", style: "tableHeader" },
        { text: "利用者\n家族", style: "tableHeader" },
        { text: "返信状態", style: "tableHeader" },
        { text: "返信日時", style: "tableHeader" },
        { text: "返信時の連絡事項", style: "tableHeader" }
      ]
    ];
    for (const item of items) {
      // 同意or確認済みの場合、連絡内容には固定文言を追加する
      let message = this.getMessagePrefix(item);
      if (item.message) {
        message += item.message;
      }

      const residentInfo: ResidentInfo = item.residentInfo as ResidentInfo;
      body.push([
        { text: this.convertDateText(item.sendDatetime), style: { alignment: "center" } },
        item.title,
        `${residentInfo.name}（${residentInfo.room}）\n${residentInfo.familyName}（${residentInfo.familyRelation}）`,
        { text: this.convertStatusText(item), style: { alignment: "center" } },
        { text: this.convertDateText(item.datetime), style: { alignment: "center" } },
        message
      ]);
    }
    tableContent.table.body = body;

    return tableContent;
  }

  /**
   * ### 日付を「yyyy年mm月dd日 hh:MM」形式の文字列に変換する
   *
   * @param datetime 日付
   */
  private convertDateText(datetime: number | null): string {
    if (datetime == null) {
      return "ー";
    }

    const date: string = this.dateUtil.convertNumberToString(datetime, "yyyy年mm月dd日 hh:MM");

    return date;
  }

  /**
   * ### 文書送付の種別を文字列に変換する
   *
   * @param datetime
   */
  private convertStatusText(item: SendDocumentsReportPreviewTableItem): string {
    if (item.datetime == null) {
      return "－";
    }

    if (item.needAgreement == true) {
      return "同意";
    } else if (item.needConfirm == true) {
      return "確認";
    } else {
      return "－";
    }
  }

  /**
   * ### 同意or確認済みの場合、固定文言を返す
   */
  private getMessagePrefix(item: SendDocumentsReportPreviewTableItem): string {
    if (item.datetime == null) {
      return "";
    }

    if (item.needAgreement == true) {
      return "文書内容に同意します。\n";
    } else if (item.needConfirm == true) {
      return "文書内容を確認しました。\n";
    } else {
      return "";
    }
  }
}

export interface SendDocumentsReportPreviewTableItem extends SendDocumentReplyGetModel {
  /** 返信日時 継承元がnumberだがnullを許容したいのでanyとする */
  datetime: any;
  /** 送付日時 */
  sendDatetime: number;
  /** タイトル */
  title: string;
  /** 確認しましたボタンを付与するか */
  needConfirm: boolean;
  /** 同意しましたボタンを付与するか */
  needAgreement: boolean;
}
